import { motion, useInView } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, Building2, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";
const HERO_ILLUSTRATION = "https://static.prod-images.emergentagent.com/jobs/4e491698-f834-49a8-a1e5-8fd3efa88feb/images/f2de8f2bd2e54b61afe81c4eb8331c09560d1c5a174c7757ebb8c615968d2e48.png";

const benefits = [
  "CRM batiment pret a l'emploi",
  "Agents IA qui travaillent 24h/24",
  "Assistants humains disponibles",
  "Organisation complète de ton activite",
];

const stats = [
  { value: '7', label: 'Agents IA' },
  { value: '24/7', label: 'Disponible' },
  { value: '100%', label: 'Batiment' },
  { value: '3min', label: 'Devis' },
];

// Animated counter component
function AnimatedCounter({ value, suffix, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function for smooth deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * value));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isInView, value, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
}

// Staggered container variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const floatVariants = {
  initial: { y: 0 },
  animate: {
    y: [-8, 8, -8],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

export default function HeroSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="relative gradient-mesh overflow-hidden pt-[72px] sm:pt-[88px] md:pt-44 pb-8 sm:pb-12 md:pb-20"
      data-testid="hero-section"
    >
      {/* Animated background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 right-10 w-72 h-72 bg-ba-accent/10 rounded-full blur-3xl" 
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.05, 0.1, 0.05],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-20 left-10 w-96 h-96 bg-ba-primary/5 rounded-full blur-3xl" 
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left Content - Staggered reveal */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-center lg:text-left"
          >
            {/* Badge with shimmer */}
            <motion.div 
              variants={itemVariants}
              className="inline-flex items-center gap-2 badge-premium badge-shimmer mb-4"
            >
              <Building2 className="w-4 h-4" />
              <span>L'assistant digital des artisans</span>
            </motion.div>

            {/* Title with reveal effect */}
            <motion.h1 
              variants={itemVariants}
              className="heading-hero mb-6"
            >
              Tu travailles sur tes chantiers,{' '}
              <span className="text-gradient">BatiAssist s'occupe</span>{' '}
              du reste
            </motion.h1>

            {/* Subtitle */}
            <motion.p 
              variants={itemVariants}
              className="body-large mb-8 max-w-xl mx-auto lg:mx-0"
            >
              BatiAssist organise ton entreprise, automatise les taches et t'aide à développer ton activité.
            </motion.p>

            {/* Benefits list - staggered */}
            <motion.div 
              variants={containerVariants}
              className="mb-8 flex flex-col items-start gap-3 w-full max-w-md mx-auto lg:mx-0"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-3 group w-full"
                >
                  <CheckCircle className="w-5 h-5 text-ba-success flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600 text-sm sm:text-base group-hover:text-slate-800 transition-colors text-left">{benefit}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTAs with glow pulse */}
            <motion.div 
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <motion.a 
                href={CALENDLY_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button 
                  className="w-full sm:w-auto bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-base btn-premium btn-glow-pulse"
                  data-testid="hero-cta-primary"
                >
                  Diagnostic gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.a>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link to="/diagnostic-entrepreneur">
                  <Button 
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-slate-200 hover:border-ba-primary text-slate-700 hover:text-ba-primary font-semibold rounded-full px-8 py-6 text-base transition-all"
                    data-testid="hero-cta-secondary"
                  >
                    <Clock className="w-5 h-5 mr-2" />
                    Test en 3 minutes
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right - Hero Image with parallax floating cards */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <motion.img 
                src={HERO_ILLUSTRATION}
                alt="BatiAssist - CRM et assistant digital pour artisans"
                className="w-full h-auto object-cover rounded-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.4 }}
              />
              
              {/* Floating card - Left - Parallax */}
              <motion.div
                variants={floatVariants}
                initial="initial"
                animate="animate"
                className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg hidden md:block"
              >
                <div className="flex items-center gap-3">
                  <motion.div 
                    className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </motion.div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">Automatisation</p>
                    <p className="text-xs text-slate-500">7 agents IA actifs</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating card - Right - Parallax delayed */}
              <motion.div
                animate={{
                  y: [8, -8, 8],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1,
                }}
                className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg hidden md:block"
              >
                <div className="flex items-center gap-3">
                  <motion.div 
                    className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </motion.div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">CRM Batiment</p>
                    <p className="text-xs text-slate-500">Concu pour toi</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.7 + index * 0.1, duration: 0.5 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="stat-premium text-center p-4 sm:p-6 cursor-default"
            >
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-ba-primary mb-1">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-slate-500 font-medium">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
